import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Snackbar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Security as SecurityIcon,
  Group as GroupIcon,
  Link as LinkIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  AdminPanelSettings as AdminIcon,
  Sync as SyncIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { Layout, StyledCard, StatusBadge } from './common';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const AdminSetup: React.FC = () => {
  const { logout, username } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const [adminEmail, setAdminEmail] = useState('');
  const [attachmentEmail, setAttachmentEmail] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error'>('success');
  const [showCustomers, setShowCustomers] = useState(false);
  const [migrationDialogOpen, setMigrationDialogOpen] = useState(false);

  const showMessage = (msg: string, type: 'success' | 'error') => {
    setMessage(msg);
    setMessageType(type);
  };

  const handleMigrateUsers = async () => {
    setMigrationLoading(true);
    try {
      const result = await apiService.migrateUsersToAdmin();
      showMessage(result.message, 'success');
      setMigrationDialogOpen(false);
    } catch (error: any) {
      showMessage(error.response?.data?.message || 'Failed to migrate users to admin', 'error');
    } finally {
      setMigrationLoading(false);
    }
  };

  const handlePromoteToAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await apiService.createUser({
        name: 'Admin User',
        email: adminEmail,
        role: 'admin',
        createFirebaseUser: false
      });
      
      showMessage(`Successfully promoted ${adminEmail} to admin role`, 'success');
      setAdminEmail('');
    } catch (error: any) {
      showMessage(error.response?.data?.message || `Failed to promote ${adminEmail} to admin`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const customerData = await apiService.getAllUsers();
      setCustomers(customerData);
      setShowCustomers(true);
    } catch (error) {
      showMessage('Failed to load customers', 'error');
    }
  };

  const handleAttachCustomer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCustomerId || !attachmentEmail) {
      showMessage('Please select a customer and enter an email', 'error');
      return;
    }

    setAttachmentLoading(true);
    try {
      const result = await apiService.attachCustomerToFirebase(selectedCustomerId, attachmentEmail);
      
      let message = result.message;
      if (result.updates && result.updates.length > 0) {
        message += '\n\nChanges made:';
        result.updates.forEach((update: string) => {
          message += `\n• ${update}`;
        });
      }
      
      showMessage(message, 'success');
      setAttachmentEmail('');
      setSelectedCustomerId('');
      await loadCustomers();
    } catch (error: any) {
      showMessage(
        error.response?.data?.message || 'Failed to attach customer to Firebase account',
        'error'
      );
    } finally {
      setAttachmentLoading(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
  const customersWithFirebase = customers.filter(c => c.firebase_uid).length;
  const customersWithoutFirebase = customers.filter(c => !c.firebase_uid).length;
  const customersWithoutEmail = customers.filter(c => !c.email || c.email.trim() === '').length;

  return (
    <Layout
      title="Admin Setup"
      username={username || undefined}
      onLogout={logout}
      onProfile={() => navigate('/profile')}
    >
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Success/Error Messages */}
        <Snackbar
          open={!!message}
          autoHideDuration={8000}
          onClose={() => setMessage('')}
        >
          <Alert
            onClose={() => setMessage('')}
            severity={messageType}
            variant="filled"
            sx={{ whiteSpace: 'pre-line' }}
          >
            {message}
          </Alert>
        </Snackbar>

        {/* Header */}
        <StyledCard sx={{ mb: 3, textAlign: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <SettingsIcon sx={{ fontSize: '3rem', color: 'primary.main', mr: 2 }} />
            <Typography variant="h3" component="h1">
              User Management Setup
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            Migrate existing users and manage Firebase accounts
          </Typography>
        </StyledCard>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 3 }}>
          {/* Migration Section */}
          <Box sx={{ flex: 1 }}>
            <StyledCard 
              title="User Migration" 
              headerAction={<SyncIcon color="primary" />}
            >
              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>Important:</strong> This will promote ALL existing Firebase users to admin role. 
                This is a one-time migration that should be run when setting up the new user system.
              </Alert>
              
              <Button
                variant="contained"
                color="warning"
                size="large"
                fullWidth
                startIcon={<SyncIcon />}
                onClick={() => setMigrationDialogOpen(true)}
                sx={{ py: 1.5 }}
              >
                Migrate All Existing Users to Admin
              </Button>
            </StyledCard>
          </Box>

          {/* Manual Admin Promotion */}
          <Box sx={{ flex: 1 }}>
            <StyledCard 
              title="Promote User to Admin"
              headerAction={<AdminIcon color="primary" />}
            >
              <Alert severity="info" sx={{ mb: 3 }}>
                <strong>Manual Promotion:</strong> Promote a specific Firebase user to admin role by email address.
              </Alert>

              <Box component="form" onSubmit={handlePromoteToAdmin} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={adminEmail}
                  onChange={(e) => setAdminEmail(e.target.value)}
                  placeholder="Enter user's email address"
                  required
                  disabled={loading}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <AdminIcon />}
                  size="large"
                >
                  {loading ? 'Promoting...' : 'Promote to Admin'}
                </Button>
              </Box>
            </StyledCard>
          </Box>
        </Box>

        {/* Customer Attachment Section */}
        <StyledCard title="Customer Firebase Integration" sx={{ mt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <strong>Customer Attachment:</strong> Connect existing customer records to Firebase user accounts 
            so they can log in with email/password. This works for customers with or without email addresses.
          </Alert>

          {!showCustomers ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Button
                variant="outlined"
                size="large"
                startIcon={<ViewIcon />}
                onClick={loadCustomers}
                sx={{ px: 4, py: 1.5 }}
              >
                Load Customer Data
              </Button>
            </Box>
          ) : (
            <>
              {/* Customer Statistics */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 4 }}>
                <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 22%' }, minWidth: '200px' }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" gutterBottom>
                        {customers.length}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Customers
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 22%' }, minWidth: '200px' }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main" gutterBottom>
                        {customersWithFirebase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        With Firebase
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 22%' }, minWidth: '200px' }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main" gutterBottom>
                        {customersWithoutFirebase}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Need Firebase
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
                <Box sx={{ flex: { xs: '1 1 45%', md: '1 1 22%' }, minWidth: '200px' }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main" gutterBottom>
                        {customersWithoutEmail}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        No Email
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>

              {/* Attachment Form */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Attach Customer to Firebase Account
                </Typography>
                
                <Box component="form" onSubmit={handleAttachCustomer} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel>Select Customer</InputLabel>
                    <Select
                      value={selectedCustomerId}
                      label="Select Customer"
                      onChange={(e) => setSelectedCustomerId(e.target.value)}
                      required
                    >
                      <MenuItem value="">
                        <em>Choose a customer...</em>
                      </MenuItem>
                      {customers.map((customer) => {
                        const hasEmail = customer.email && customer.email.trim() !== '';
                        const hasFirebase = customer.firebase_uid;
                        
                        return (
                          <MenuItem key={customer.id} value={customer.id}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                              <Typography>{customer.name}</Typography>
                              <StatusBadge status={customer.status} />
                              {hasFirebase ? (
                                <Chip icon={<CheckCircleIcon />} label="Firebase" color="success" size="small" />
                              ) : (
                                <Chip icon={<CancelIcon />} label="No Firebase" color="error" size="small" />
                              )}
                              {!hasEmail && (
                                <Chip icon={<WarningIcon />} label="No Email" color="warning" size="small" />
                              )}
                            </Box>
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {selectedCustomer && (
                    <Alert 
                      severity={selectedCustomer.firebase_uid ? "warning" : "info"}
                      sx={{ mb: 2 }}
                    >
                      <Typography variant="subtitle2" gutterBottom>
                        Selected Customer Details:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><PersonIcon /></ListItemIcon>
                          <ListItemText primary={`Name: ${selectedCustomer.name}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><StatusBadge status={selectedCustomer.status} /></ListItemIcon>
                          <ListItemText primary={`Status: ${selectedCustomer.status}`} />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            {selectedCustomer.email ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                          </ListItemIcon>
                          <ListItemText 
                            primary={`Email: ${selectedCustomer.email || 'No email address'}`} 
                          />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon>
                            {selectedCustomer.firebase_uid ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
                          </ListItemIcon>
                          <ListItemText 
                            primary={`Firebase: ${selectedCustomer.firebase_uid ? 'Connected' : 'Not connected'}`} 
                          />
                        </ListItem>
                      </List>
                      {selectedCustomer.firebase_uid && (
                        <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                          ⚠️ This customer is already connected to a Firebase account. Attaching to a new account will override the existing connection.
                        </Typography>
                      )}
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    label="Firebase User Email"
                    type="email"
                    value={attachmentEmail}
                    onChange={(e) => setAttachmentEmail(e.target.value)}
                    placeholder="Enter Firebase user's email address"
                    required
                    disabled={attachmentLoading}
                    helperText={
                      selectedCustomer && (!selectedCustomer.email || selectedCustomer.email.trim() === '') 
                        ? "⚠️ Note: This customer has no email. After attachment, the customer record will be updated with the Firebase user's email."
                        : "💡 Tip: This should be the email address of an existing Firebase user account."
                    }
                    InputProps={{
                      startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                    }}
                  />

                  <Button
                    type="submit"
                    variant="contained"
                    disabled={attachmentLoading || !selectedCustomerId || !attachmentEmail}
                    startIcon={attachmentLoading ? <CircularProgress size={20} /> : <LinkIcon />}
                    size="large"
                  >
                    {attachmentLoading ? 'Attaching...' : 'Attach Customer to Firebase Account'}
                  </Button>
                </Box>
              </Paper>

              {/* Advanced Options */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Advanced Options & Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        What happens during attachment:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Customer record gets linked to Firebase user UID" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Firebase user gets customer role and permissions" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Customer's API key and widget settings remain unchanged" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><InfoIcon color="info" /></ListItemIcon>
                          <ListItemText primary="If customer has no email, it will be updated with Firebase user's email" />
                        </ListItem>
                      </List>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" gutterBottom fontWeight={600}>
                        Compatible scenarios:
                      </Typography>
                      <List dense>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Customer has email, Firebase user has same email" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Customer has email, Firebase user has different email" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                          <ListItemText primary="Customer has no email, Firebase user has any email" />
                        </ListItem>
                        <ListItem>
                          <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                          <ListItemText primary="Firebase user already has admin role (will be rejected)" />
                        </ListItem>
                      </List>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Customer List */}
              <Accordion sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    View All Customers ({customers.length})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Email</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell>Firebase</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers.map((customer) => {
                          const hasEmail = customer.email && customer.email.trim() !== '';
                          const hasFirebase = customer.firebase_uid;
                          
                          return (
                            <TableRow key={customer.id} hover>
                              <TableCell>
                                <Box>
                                  <Typography variant="body2" fontWeight={500}>
                                    {customer.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {customer.id}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell>
                                {hasEmail ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <EmailIcon sx={{ mr: 1, color: 'success.main' }} />
                                    <Typography variant="body2">{customer.email}</Typography>
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <CancelIcon sx={{ mr: 1, color: 'error.main' }} />
                                    <Typography variant="body2" color="error.main">No email</Typography>
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell>
                                <StatusBadge status={customer.status} />
                              </TableCell>
                              <TableCell>
                                {hasFirebase ? (
                                  <Chip 
                                    icon={<CheckCircleIcon />} 
                                    label="Connected" 
                                    color="success" 
                                    size="small" 
                                  />
                                ) : (
                                  <Chip 
                                    icon={<CancelIcon />} 
                                    label="Not connected" 
                                    color="error" 
                                    size="small" 
                                  />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>
            </>
          )}
        </StyledCard>

        {/* Migration Confirmation Dialog */}
        <Dialog
          open={migrationDialogOpen}
          onClose={() => setMigrationDialogOpen(false)}
          aria-labelledby="migration-dialog-title"
          aria-describedby="migration-dialog-description"
        >
          <DialogTitle id="migration-dialog-title">
            Migrate All Users to Admin Role
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="migration-dialog-description">
              This action will promote ALL existing Firebase users to admin role. This is typically done once 
              during initial system setup. Are you sure you want to proceed?
            </DialogContentText>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone and will affect all existing users in the system.
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMigrationDialogOpen(false)} disabled={migrationLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleMigrateUsers} 
              color="warning" 
              variant="contained"
              disabled={migrationLoading}
              startIcon={migrationLoading ? <CircularProgress size={20} /> : <SyncIcon />}
            >
              {migrationLoading ? 'Migrating...' : 'Migrate Users'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Layout>
  );
};

export default AdminSetup; 